<template>
  <div class="content">
    <div class="d-flex">
      <h4>Feed <span v-if="FeedTitle"> - {{ FeedTitle }} ({{ FeedNodeCount | format }})</span></h4>
      <b-button
        v-show="feedMeta.export_instructions"
        size="sm"
        variant="primary"
        title="Export to spreadsheet"
        class="btn-primary ml-2 text-nowrap"
        :disabled="exporting"
        @click="exportFeed"
      >
        <span v-if="exporting">Exporting..</span>
        <span v-else>Export</span>
      </b-button>
    </div>

    <!-- Search bar -->
     <b-input-group class="mt-3">
      <template #append>
        <b-button size="sm" variant="white" title="Clear text" @click="clearSearchText">
          Clear filter
        </b-button>
      </template>
      <b-form-input
        v-model="searchText"
        placeholder="Filter items"
        autocomplete="off"
        trim
        debounce="500"
        autofocus
        @update="filterItems"
      ></b-form-input>
    </b-input-group>
    <!-- Feed -->
    <div class="mt-4">
      <h5 v-if="loadingCount > 0">Fetching feed..</h5>
      <h5 v-else-if="feedData === null">Feed unavailable.</h5>
      <div v-else-if="feedData.length === 0">
        <feed-no-results :searchText="searchText" />
      </div>
      <div v-else>
        <div v-for="node in feedData" :key="node.id">
          <b-card class="mt-2">
            <div class="d-flex gap-4 justify-content-between">
              <h4># {{ node.id }}</h4>
              <div>
                <b-dropdown variant="white" size="sm" text="Exports" right>
                  <b-dropdown-item
                    v-for="feedExport in feedExports"
                    :key="feedExport.id"
                    @click="editExport(feedExport.id, node.node_xml)"
                  >
                    {{ feedExport.title }}
                  </b-dropdown-item>
                  <b-dropdown-divider v-if="feedExports.length > 0"></b-dropdown-divider>
                  <b-dropdown-item @click="createExport(node.node_xml)">
                    Create new export
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            <b-card-text>
              <pre v-highlightjs="node.node_xml"><code class="language-xml"></code></pre>
            </b-card-text>
          </b-card>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <b-pagination
      v-model="feedPage"
      :disabled="loadingCount > 0"
      :total-rows="feedRows"
      :per-page="feedPerPage"
      aria-controls="my-table"
      first-number
      last-number
      prev-text="Previous"
      next-text="Next"
      class="mt-2"
      @change="scheduleFetchData"
    ></b-pagination>

    <edit-export
      :data="editExportModal"
      @close="fetchExports()"
    />
  </div>
</template>

<script>
const FeedNoResults = () => import('@/components/feed/NoResults.vue');
const EditExport = () => import('@/components/feed_export/EditExport.vue');

export default {
  name: 'Feed',
  components: {
    FeedNoResults,
    EditExport,
  },
  computed: {
    FeedID() {
      return this.$route.params.id;
    },
    FeedTitle() {
      return Object.prototype.hasOwnProperty.call(this.feedMeta, 'title') ? this.feedMeta.title : '';
    },
    FeedNodeCount() {
      return Object.prototype.hasOwnProperty.call(this.feedMeta, 'node_count') ? this.feedMeta.node_count : 0;
    },
  },
  data() {
    return {
      loadingCount: 0,
      exporting: false,
      searchText: '',
      feedMeta: {},
      feedData: [],
      feedRows: 0,
      feedPage: 1,
      feedPerPage: 5,
      feedExports: [],
      editExportModal: {
        showModal: false,
        feedId: null,
        exportId: null,
        nodeString: null,
      },
    };
  },
  methods: {
    fetchMeta() {
      this.loadingCount += 1;
      this.$http
        .get(`/feed/${this.FeedID}/meta`)
        .then((res) => {
          this.feedMeta = res.body.feedMeta;
        })
        .catch((err) => {
          alert(`Failed to fetch feed meta: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount -= 1;
        });
    },
    scheduleFetchData() {
      setTimeout(() => {
        this.fetchData();
      }, 0);
    },
    fetchData() {
      this.loadingCount += 1;
      this.$http
        .get(`/feed/${this.FeedID}/data`)
        .query({ searchText: this.searchText })
        .query({ page: this.feedPage })
        .query({ perPage: this.feedPerPage })
        .then((res) => {
          this.feedData = res.body.feedData;
          this.feedRows = res.body.feedRows;
        })
        .catch((err) => {
          alert(`Failed to fetch feed data: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount -= 1;
        });
    },
    fetchExports() {
      this.$http
        .get(`/feed_export/${this.FeedID}/exports`)
        .then((res) => {
          this.feedExports = res.body;
        })
        .catch((err) => {
          alert(`Failed to fetch exports: ${err.response.text}`);
        });
    },
    editExport(exportId, nodeString) {
      this.editExportModal.feedId = this.FeedID;
      this.editExportModal.exportId = exportId;
      this.editExportModal.nodeString = nodeString;
      this.editExportModal.showModal = true;
    },
    createExport(nodeString) {
      this.loadingCount++;
      this.$http
        .post(`/feed_export/${this.FeedID}/exports`)
        .then((res) => {
          this.fetchExports();
          const exportId = res.body;
          this.editExport(exportId, nodeString);
        })
        .catch((err) => {
          alert(`Failed to create export: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    exportFeed() {
      if (this.exporting) return;
      this.exporting = true;
      this.$http
        .get(`/feed/${this.FeedID}/export`)
        .then((res) => {
          const link = document.createElement('a');
          link.href = res.body.url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          alert('Failed to export feed');
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    filterItems() {
      this.feedPage = 1;
      this.fetchData();
    },
    clearSearchText() {
      this.feedPage = 1;
      this.searchText = '';
      this.fetchData();
    },
  },
  created() {
    this.fetchMeta();
    this.fetchData();
    this.fetchExports();
  },
};
</script>

<style lang="scss" scoped>
code {
  display: block;
  white-space: pre-wrap
}
</style>
